import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";

export default function Landing() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      // Redirect based on subscription status
      if (user.subscription_status === "active") {
        navigate("/dashboard");
      } else {
        navigate("/learn");
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back, trader");
      } else {
        await register(email, password, name);
        toast.success("Account created. Time to beat the game.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Grid pattern background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex flex-1 flex-col justify-center px-16 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent" />
          <div className="relative">
            <p className="text-[10px] font-mono text-slate-600 tracking-widest mb-2">A QUARTER TO CLOSE PRODUCT</p>
            <h1 className="font-mono text-5xl font-bold tracking-tight mb-6">
              <span className="text-blue-500">INSTITUTIONAL</span>
              <br />
              <span className="text-white">EDGE</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-md mb-8">
              The market is rigged. Now you have the playbook to beat it.
            </p>
            
            {/* Features */}
            <div className="space-y-4">
              {[
                "Order Block & Liquidity Analysis",
                "AI-Powered Trading Coach",
                "Sniper Entry Checklist",
                "Institutional Trade Journal",
              ].map((feature, i) => (
                <div 
                  key={i} 
                  className="flex items-center gap-3 text-slate-300"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-1.5 h-1.5 bg-blue-500" />
                  <span className="font-mono text-sm">{feature}</span>
                </div>
              ))}
            </div>

            {/* Quote */}
            <blockquote className="mt-12 border-l-2 border-blue-500/50 pl-4">
              <p className="text-slate-500 italic">
                "The market does not beat them. They beat themselves."
              </p>
              <cite className="text-slate-600 text-sm mt-2 block">— Jesse Livermore</cite>
            </blockquote>
          </div>
        </div>

        {/* Right side - Auth Form */}
        <div className="flex-1 flex items-center justify-center px-8 lg:px-16">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-8">
              <h1 className="font-mono text-3xl font-bold">
                <span className="text-blue-500">INSTITUTIONAL</span>
                <span className="text-white"> EDGE</span>
              </h1>
            </div>

            {/* Auth Card */}
            <div className="bg-[#0A0A0A] border border-[#262626] p-8">
              <div className="flex gap-4 mb-8">
                <button
                  onClick={() => setIsLogin(true)}
                  data-testid="login-tab"
                  className={`flex-1 py-2 font-mono text-sm tracking-wider border-b-2 transition-colors ${
                    isLogin 
                      ? "text-white border-blue-500" 
                      : "text-slate-500 border-transparent hover:text-slate-300"
                  }`}
                >
                  LOGIN
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  data-testid="register-tab"
                  className={`flex-1 py-2 font-mono text-sm tracking-wider border-b-2 transition-colors ${
                    !isLogin 
                      ? "text-white border-blue-500" 
                      : "text-slate-500 border-transparent hover:text-slate-300"
                  }`}
                >
                  REGISTER
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <label className="block text-xs font-mono text-slate-500 mb-2">TRADER NAME</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="name-input"
                      className="w-full input-institutional px-4 py-3"
                      placeholder="Enter your name"
                      required={!isLogin}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">EMAIL</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                    className="w-full input-institutional px-4 py-3"
                    placeholder="trader@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">PASSWORD</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="password-input"
                    className="w-full input-institutional px-4 py-3"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-btn"
                  className="w-full btn-primary py-3 mt-6 disabled:opacity-50"
                >
                  {loading ? "PROCESSING..." : isLogin ? "ENTER THE ARENA" : "JOIN THE ELITE"}
                </button>
              </form>

              <p className="text-center text-slate-600 text-xs mt-6 font-mono">
                {isLogin ? "New to the game?" : "Already have an account?"}{" "}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-blue-500 hover:text-blue-400"
                >
                  {isLogin ? "Register" : "Login"}
                </button>
              </p>
            </div>

            {/* Warning */}
            <div className="mt-6 p-4 border border-yellow-900/30 bg-yellow-900/10">
              <p className="text-yellow-500/80 text-xs font-mono">
                ⚠ RISK WARNING: Trading involves substantial risk. Only trade with capital you can afford to lose.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
